import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import SelectInput from '@/components/General/inputs/SelectInput';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Unicon from '@/components/General/Unicon';
import VariableTextInsertButton from '@/components/Paths/VariableTextInsertButton';
import AutomationBindingInput from './AutomationBindingInput';
import PersonTargetInput from './PersonTargetInput';
import PeopleTargetInput from './PeopleTargetInput';
import { filterVariablesForParam, isBinding } from '@/utils/automationBuilder';

// UPSERT_PERSON writes a bounded People column allowlist (worker-side
// UPSERT_PEOPLE_COLUMNS — keep in sync).
const UPSERT_FIELD_OPTIONS = [
    { value: 'firstName', label: 'First Name' },
    { value: 'lastName', label: 'Last Name' },
    { value: 'email', label: 'Email' },
    { value: 'dateOfBirth', label: 'Date of Birth' },
    { value: 'gender', label: 'Gender' },
    { value: 'maritalStatus', label: 'Marital Status' },
    { value: 'occupation', label: 'Occupation' },
    { value: 'employer', label: 'Employer' },
    { value: 'anniversary', label: 'Anniversary' },
];

const DUE_TYPE_OPTIONS = [
    { value: 'AUTOMATIONS', label: 'Automation task' },
    { value: 'INDIVIDUAL', label: 'Individual task' },
    { value: 'FOLLOW_UP', label: 'Follow Up care task' },
];

const DUE_MODE_OPTIONS = [
    { value: 'RELATIVE', label: 'Due after a delay' },
    { value: 'ABSOLUTE', label: 'Due on a date' },
    { value: 'NONE', label: 'No due date' },
];

const TIME_UNIT_OPTIONS = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' },
];

const FOLLOW_UP_PURPOSE_OPTIONS = [
    { value: 'OUTREACH', label: 'Outreach' },
    { value: 'APPROVAL', label: 'Approval' },
    { value: 'MEAL', label: 'Meal' },
    { value: 'OTHER', label: 'Other' },
];

/**
 * V2ActionConfigPanel — config editors for the six §14.6/§14.7 v2 action
 * types. Emits raw draft config via updateConfig(key, value); the parent
 * step screen shapes it through the matching build*Config helper on save.
 *
 * Props:
 *  - actionType, config, updateConfig(key, value)
 *  - variables: filtered v2 variable list (AutomationBindingInput-ready)
 *  - lookups: {paths, followUpPaths, pathsTaskQuery, stringVariables,
 *              storeKey, pathID}
 */
const V2ActionConfigPanel = ({ actionType, config = {}, updateConfig, variables = [], lookups = {} }) => {
    const { paths = [], followUpPaths = [], pathsTaskQuery, stringVariables = [], storeKey, pathID } = lookups;

    const set = (key, value) => updateConfig(key, value);
    const setNested = (parent, key, value) => updateConfig(parent, { ...(config[parent] || {}), [key]: value });

    const stringVars = stringVariables.length ? stringVariables : filterVariablesForParam(variables, { type: 'string' });
    const personVars = filterVariablesForParam(variables, { semantic: 'personID' });

    const personBindingValue = (key, parent) => {
        const raw = parent ? config[parent]?.[key] : config[key];
        return isBinding(raw) ? raw : undefined;
    };
    const personLiteralValue = (key, parent) => {
        const raw = parent ? config[parent]?.[key] : config[key];
        return raw && typeof raw === 'object' && raw.id ? raw : null;
    };
    const setPersonTarget = (key, parent) => (val) => {
        // val is {var:key} from the binding picker.
        if (parent) setNested(parent, key, val);
        else set(key, val);
    };

    const invokeTarget = useMemo(
        () => (paths || []).find(p => p.id === (isBinding(config.automationID) ? null : config.automationID)) || null,
        [paths, config.automationID]);
    const invokeInputFields = invokeTarget?.inputSchema?.fields || [];

    const updateFieldValue = (fields, key, value) => {
        const next = { ...(fields || {}) };
        if (value === undefined || value === null || value === '') delete next[key];
        else next[key] = value;
        return next;
    };

    const updateFields = config.update || {};
    const usedUpdateCols = new Set(Object.keys(updateFields));
    const availableUpdateCols = UPSERT_FIELD_OPTIONS.filter(f => !usedUpdateCols.has(f.value));

    switch (actionType) {
        case 'RESOLVE_PERSON':
            return (
                <View>
                    <Text style={styles.helpText}>
                        Looks up a person from the identity fields below. The step records its outcome (personID, resolution, review flag) as outputs other steps can bind.
                    </Text>
                    {['firstName', 'lastName', 'email', 'phone'].map(field => (
                        <View key={field} style={styles.textWithVariableRow}>
                            <View style={styles.textInputFlex}>
                                <Input
                                    title={field === 'firstName' ? 'First Name' : field === 'lastName' ? 'Last Name' : field === 'email' ? 'Email' : 'Phone'}
                                    placeholder={`Enter ${field} or insert a variable...`}
                                    value={config[field] || ''}
                                    onUpdate={(val) => set(field, val)}
                                />
                            </View>
                            <VariableTextInsertButton
                                variables={stringVars}
                                value={config[field] || ''}
                                onUpdate={(val) => set(field, val)}
                                testID={`resolve-person-${field}-variable-button`}
                            />
                        </View>
                    ))}
                    <Text style={styles.helpText}>
                        An ambiguous or conflicting match never picks a person — it records a review outcome you can branch on.
                    </Text>
                </View>
            );

        case 'UPSERT_PERSON':
            return (
                <View>
                    <Text style={styles.helpText}>
                        Matches an existing person, then optionally updates their record or creates a new person when no match is found.
                    </Text>
                    <Text style={styles.sectionLabel}>Match on</Text>
                    {['firstName', 'lastName', 'email', 'phone'].map(field => (
                        <View key={field} style={styles.textWithVariableRow}>
                            <View style={styles.textInputFlex}>
                                <Input
                                    title={field === 'firstName' ? 'First Name' : field === 'lastName' ? 'Last Name' : field === 'email' ? 'Email' : 'Phone'}
                                    placeholder={`Enter ${field} or insert a variable...`}
                                    value={config.matchBy?.[field] || ''}
                                    onUpdate={(val) => setNested('matchBy', field, val)}
                                />
                            </View>
                            <VariableTextInsertButton
                                variables={stringVars}
                                value={config.matchBy?.[field] || ''}
                                onUpdate={(val) => setNested('matchBy', field, val)}
                                testID={`upsert-match-${field}-variable-button`}
                            />
                        </View>
                    ))}

                    <ToggleRow
                        label="Update when matched"
                        description="Write fields onto the matched person record."
                        value={config.updateWhenMatched === true}
                        onValueChange={(val) => set('updateWhenMatched', val)}
                    />
                    {config.updateWhenMatched === true && (
                        <View style={styles.nestedPanel}>
                            {Object.entries(updateFields).map(([col, value]) => (
                                <View key={col} style={styles.updateRow}>
                                    <SelectInput
                                        title="Field"
                                        value={col}
                                        options={UPSERT_FIELD_OPTIONS}
                                        onUpdate={(newCol) => {
                                            const next = { ...updateFields };
                                            const v = next[col];
                                            delete next[col];
                                            next[newCol] = v ?? '';
                                            set('update', next);
                                        }}
                                        hideClearButton
                                    />
                                    <View style={styles.textWithVariableRow}>
                                        <View style={styles.textInputFlex}>
                                            <Input
                                                title="Value"
                                                placeholder="Enter value or insert a variable..."
                                                value={isBinding(value) ? `{{${value.var}}}` : (value?.toString() || '')}
                                                onUpdate={(val) => set('update', updateFieldValue(updateFields, col, val))}
                                            />
                                        </View>
                                        <VariableTextInsertButton
                                            variables={stringVars}
                                            value={isBinding(value) ? `{{${value.var}}}` : (value?.toString() || '')}
                                            onUpdate={(val) => set('update', updateFieldValue(updateFields, col, val))}
                                        />
                                    </View>
                                    <TouchableOpacity
                                        hitSlop={10}
                                        onPress={() => set('update', updateFieldValue(updateFields, col, null))}
                                        testID={`upsert-update-remove-${col}`}
                                    >
                                        <Unicon name="close-circle" size={18} colorVariant="error" />
                                    </TouchableOpacity>
                                </View>
                            ))}
                            {availableUpdateCols.length > 0 && (
                                <TouchableOpacity
                                    style={styles.addRowBtn}
                                    onPress={() => set('update', updateFieldValue(updateFields, availableUpdateCols[0].value, ''))}
                                    testID="upsert-update-add-field"
                                >
                                    <Unicon name="add-circle-outline" size={18} colorVariant="primary" />
                                    <Text style={styles.addRowText}>Add field to update</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    )}

                    <ToggleRow
                        label="Create when unmatched"
                        description="Create a new person record when nobody matches."
                        value={config.createWhenUnmatched === true}
                        onValueChange={(val) => set('createWhenUnmatched', val)}
                    />
                    {config.createWhenUnmatched === true && (
                        <View style={styles.nestedPanel}>
                            <Text style={styles.helpText}>
                                Leave a field blank to reuse the match values above.
                            </Text>
                            {['firstName', 'lastName', 'email', 'phone', 'dateOfBirth'].map(field => (
                                <View key={field} style={styles.textWithVariableRow}>
                                    <View style={styles.textInputFlex}>
                                        <Input
                                            title={field === 'dateOfBirth' ? 'Date of Birth' : field === 'firstName' ? 'First Name' : field === 'lastName' ? 'Last Name' : field === 'email' ? 'Email' : 'Phone'}
                                            placeholder={field === 'dateOfBirth' ? 'YYYY-MM-DD' : `Enter ${field} or insert a variable...`}
                                            value={config.create?.[field] || ''}
                                            onUpdate={(val) => setNested('create', field, val)}
                                        />
                                    </View>
                                    <VariableTextInsertButton
                                        variables={stringVars}
                                        value={config.create?.[field] || ''}
                                        onUpdate={(val) => setNested('create', field, val)}
                                    />
                                </View>
                            ))}
                        </View>
                    )}
                </View>
            );

        case 'CREATE_TASK': {
            const dueMode = config.dueMode || (config.dueDate ? 'ABSOLUTE' : 'RELATIVE');
            return (
                <View>
                    <Text style={styles.helpText}>
                        Creates a task and records its Task Instance ID as an output. The run continues immediately — add a Wait for Task step to pause on it.
                    </Text>
                    <View style={styles.textWithVariableRow}>
                        <View style={styles.textInputFlex}>
                            <Input
                                title="Title"
                                placeholder="e.g. Call {{firstName}} this week"
                                value={config.title || ''}
                                onUpdate={(val) => set('title', val)}
                            />
                        </View>
                        <VariableTextInsertButton
                            variables={stringVars}
                            value={config.title || ''}
                            onUpdate={(val) => set('title', val)}
                            testID="create-task-title-variable-button"
                        />
                    </View>
                    <View style={styles.textWithVariableRow}>
                        <View style={styles.textInputFlex}>
                            <Input
                                title="Description"
                                placeholder="Details for the assignee..."
                                multiline
                                value={config.description || ''}
                                onUpdate={(val) => set('description', val)}
                            />
                        </View>
                        <VariableTextInsertButton
                            variables={stringVars}
                            value={config.description || ''}
                            onUpdate={(val) => set('description', val)}
                        />
                    </View>

                    <SelectInput
                        title="Task Type"
                        helpText="Automation tasks are internal work. Follow Up tasks attach to a person's care record."
                        value={config.dueType || 'AUTOMATIONS'}
                        options={DUE_TYPE_OPTIONS}
                        onUpdate={(val) => set('dueType', val)}
                    />

                    <SheetSelector
                        type="tasks"
                        title="Task Definition (optional)"
                        addScreenName="tasks/create"
                        addScreenParams={{ dueType: 'AUTOMATIONS' }}
                        fromScreen="paths/steps/create"
                        fromStoreKey={storeKey}
                        queryResult={pathsTaskQuery}
                        value={config.task || null}
                        onChange={(task) => updateConfig('task', task)}
                    />
                    <Text style={styles.helpText}>
                        Leave empty to create a one-off task defined by this step.
                    </Text>

                    <PeopleTargetInput
                        title="Assignees"
                        multiple
                        variables={variables}
                        value={config.assigneePersonIDs?.length ? config.assigneePersonIDs : (config.assigneePersonID ? [config.assigneePersonID] : [])}
                        onUpdate={(val) => set('assigneePersonIDs', val)}
                        testID="create-task-assignees"
                        fromScreen="paths/steps/create"
                        fromStoreKey={storeKey}
                    />
                    <Text style={styles.helpText}>
                        Unassigned tasks are claimable by the team.
                    </Text>

                    <SelectInput
                        title="Due"
                        value={dueMode}
                        options={DUE_MODE_OPTIONS}
                        onUpdate={(val) => set('dueMode', val)}
                    />
                    {dueMode === 'ABSOLUTE' && (
                        <DatePickerInput
                            title="Due Date"
                            mode="datetime"
                            minuteInterval={5}
                            value={config.dueDate ? new Date(config.dueDate) : null}
                            onUpdate={(val) => set('dueDate', val ? new Date(val).toISOString() : null)}
                        />
                    )}
                    {dueMode === 'RELATIVE' && (
                        <View style={styles.inlineRow}>
                            <View style={styles.inlineInputFlex}>
                                <Input
                                    title="Due In"
                                    placeholder="1"
                                    keyboardType="numeric"
                                    value={String(config.dueIn?.duration || '')}
                                    onUpdate={(val) => setNested('dueIn', 'duration', val)}
                                />
                            </View>
                            <View style={styles.inlineSelectFixed}>
                                <SelectInput
                                    title="Unit"
                                    value={config.dueIn?.unit || 'days'}
                                    options={TIME_UNIT_OPTIONS}
                                    onUpdate={(val) => setNested('dueIn', 'unit', val)}
                                />
                            </View>
                        </View>
                    )}

                    {config.dueType === 'FOLLOW_UP' && (
                        <View style={styles.nestedPanel}>
                            <Text style={styles.sectionLabel}>Follow Up context</Text>
                            <PersonTargetInput
                                title="Care Person"
                                variables={variables}
                                value={personBindingValue('personID', 'followUp')}
                                onUpdate={setPersonTarget('personID', 'followUp')}
                                personValue={personLiteralValue('personID', 'followUp')}
                                onPersonChange={(person) => setNested('followUp', 'personID', person?.id ? { id: person.id, ...person } : undefined)}
                                required
                                testID="create-task-followup-person"
                                fromScreen="paths/steps/create"
                                fromStoreKey={storeKey}
                            />
                            <SelectInput
                                title="Purpose"
                                value={config.followUp?.purpose || 'OTHER'}
                                options={FOLLOW_UP_PURPOSE_OPTIONS}
                                onUpdate={(val) => setNested('followUp', 'purpose', val)}
                            />
                            <AutomationBindingInput
                                title="Follow Up Enrollment (optional)"
                                placeholder="Bind an enrollment variable..."
                                variables={filterVariablesForParam(variables, { semantic: 'followUpEnrollmentID' })}
                                value={isBinding(config.followUp?.followUpEnrollmentID) ? config.followUp.followUpEnrollmentID : undefined}
                                onUpdate={(val) => setNested('followUp', 'followUpEnrollmentID', val)}
                                allowManualEntry={false}
                                testID="create-task-followup-enrollment"
                            />
                            <Input
                                title="Safe Logistics (optional)"
                                placeholder="Shared scheduling details the whole care team can see..."
                                multiline
                                value={config.followUp?.safeLogistics || ''}
                                onUpdate={(val) => setNested('followUp', 'safeLogistics', val)}
                            />
                        </View>
                    )}
                </View>
            );
        }

        case 'WAIT_FOR_TASK': {
            const deadlineMode = config.deadlineMode || (config.deadlineAt ? 'ABSOLUTE' : 'RELATIVE');
            return (
                <View>
                    <Text style={styles.helpText}>
                        Pauses the run until the bound task completes, is cancelled, or the deadline passes. Waiting on a task that already finished resolves immediately.
                    </Text>
                    <AutomationBindingInput
                        title="Task Instance"
                        placeholder="Bind a task instance output..."
                        helpText="Usually the Task Instance ID output of an upstream Create Task step."
                        variables={filterVariablesForParam(variables, { semantic: 'taskInstanceID' })}
                        value={isBinding(config.taskInstanceID) ? config.taskInstanceID : undefined}
                        onUpdate={(val) => set('taskInstanceID', val)}
                        required
                        allowManualEntry={false}
                        testID="wait-for-task-instance"
                    />
                    <SelectInput
                        title="Wait Deadline"
                        helpText="How long the run may wait before recording a timed-out outcome."
                        value={deadlineMode}
                        options={[
                            { value: 'RELATIVE', label: 'Wait up to a duration' },
                            { value: 'ABSOLUTE', label: 'Wait until a date' },
                            { value: 'NONE', label: 'No deadline' },
                        ]}
                        onUpdate={(val) => set('deadlineMode', val)}
                    />
                    {deadlineMode === 'ABSOLUTE' && (
                        <DatePickerInput
                            title="Deadline"
                            mode="datetime"
                            minuteInterval={5}
                            value={config.deadlineAt ? new Date(config.deadlineAt) : null}
                            onUpdate={(val) => set('deadlineAt', val ? new Date(val).toISOString() : null)}
                        />
                    )}
                    {deadlineMode === 'RELATIVE' && (
                        <View style={styles.inlineRow}>
                            <View style={styles.inlineInputFlex}>
                                <Input
                                    title="Wait Up To"
                                    placeholder="7"
                                    keyboardType="numeric"
                                    value={String(config.timeoutDuration || '')}
                                    onUpdate={(val) => set('timeoutDuration', val)}
                                />
                            </View>
                            <View style={styles.inlineSelectFixed}>
                                <SelectInput
                                    title="Unit"
                                    value={config.timeoutUnit || 'days'}
                                    options={TIME_UNIT_OPTIONS}
                                    onUpdate={(val) => set('timeoutUnit', val)}
                                />
                            </View>
                        </View>
                    )}
                </View>
            );
        }

        case 'REQUEST_APPROVAL': {
            const dueMode = config.dueMode || (config.dueDate ? 'ABSOLUTE' : 'NONE');
            return (
                <View>
                    <Text style={styles.helpText}>
                        Creates an approval task for the people below — the run pauses until one decides, then branches on Approved/Denied. First approver to respond decides. Overdue approvals stay pending.
                    </Text>
                    <View style={styles.textWithVariableRow}>
                        <View style={styles.textInputFlex}>
                            <Input
                                title="Title"
                                placeholder="e.g. Approve {{firstName}}'s role change"
                                value={config.title || ''}
                                onUpdate={(val) => set('title', val)}
                            />
                        </View>
                        <VariableTextInsertButton
                            variables={stringVars}
                            value={config.title || ''}
                            onUpdate={(val) => set('title', val)}
                            testID="request-approval-title-variable-button"
                        />
                    </View>
                    <View style={styles.textWithVariableRow}>
                        <View style={styles.textInputFlex}>
                            <Input
                                title="Request"
                                placeholder="What should the approvers decide?"
                                multiline
                                value={config.request || ''}
                                onUpdate={(val) => set('request', val)}
                            />
                        </View>
                        <VariableTextInsertButton
                            variables={stringVars}
                            value={config.request || ''}
                            onUpdate={(val) => set('request', val)}
                            testID="request-approval-request-variable-button"
                        />
                    </View>

                    <View testID="request-approval-approvers">
                        <PeopleTargetInput
                            title="Approvers"
                            required
                            multiple
                            variables={variables}
                            value={config.approverPersonIDs || []}
                            onUpdate={(val) => set('approverPersonIDs', val)}
                            testID="request-approval-approvers-input"
                            fromScreen="paths/steps/create"
                            fromStoreKey={storeKey}
                        />
                    </View>

                    <SelectInput
                        title="Due"
                        helpText="Overdue approvals stay pending — the run keeps waiting until someone decides."
                        value={dueMode}
                        options={DUE_MODE_OPTIONS}
                        onUpdate={(val) => set('dueMode', val)}
                    />
                    {dueMode === 'ABSOLUTE' && (
                        <DatePickerInput
                            title="Due Date"
                            mode="datetime"
                            minuteInterval={5}
                            value={config.dueDate ? new Date(config.dueDate) : null}
                            onUpdate={(val) => set('dueDate', val ? new Date(val).toISOString() : null)}
                        />
                    )}
                    {dueMode === 'RELATIVE' && (
                        <View style={styles.inlineRow}>
                            <View style={styles.inlineInputFlex}>
                                <Input
                                    title="Due In"
                                    placeholder="1"
                                    keyboardType="numeric"
                                    value={String(config.dueIn?.duration || '')}
                                    onUpdate={(val) => setNested('dueIn', 'duration', val)}
                                />
                            </View>
                            <View style={styles.inlineSelectFixed}>
                                <SelectInput
                                    title="Unit"
                                    value={config.dueIn?.unit || 'days'}
                                    options={TIME_UNIT_OPTIONS}
                                    onUpdate={(val) => setNested('dueIn', 'unit', val)}
                                />
                            </View>
                        </View>
                    )}
                </View>
            );
        }

        case 'START_FOLLOW_UP_PATH': {
            const pathOptions = (followUpPaths || [])
                .filter(p => p && !p.isArchived)
                .map(p => ({ value: p.pathID, label: p.name }));
            return (
                <View>
                    <Text style={styles.helpText}>
                        Starts the chosen person on a Follow Up path. This is a different target than an Automation — the Follow Up path picker only lists Follow Up templates.
                    </Text>
                    <PersonTargetInput
                        title="Person to Enroll"
                        variables={variables}
                        value={personBindingValue('personID')}
                        onUpdate={setPersonTarget('personID')}
                        personValue={personLiteralValue('personID')}
                        onPersonChange={(person) => set('personID', person?.id ? { id: person.id, ...person } : undefined)}
                        required
                        testID="start-follow-up-person"
                        fromScreen="paths/steps/create"
                        fromStoreKey={storeKey}
                    />
                    {personVars.length === 0 && (
                        <Text style={styles.staleWarning} testID="start-follow-up-no-person-vars">
                            No person variables are available yet — add a step that produces a Person ID (like Resolve Person) or pick a specific person.
                        </Text>
                    )}
                    <SelectInput
                        title="Follow Up Path"
                        placeholder="Select a Follow Up path..."
                        value={config.followUpPathID}
                        options={pathOptions}
                        onUpdate={(val) => set('followUpPathID', val)}
                        required
                    />
                    {pathOptions.length === 0 && (
                        <Text style={styles.helpText} testID="start-follow-up-no-paths">
                            No Follow Up paths are available. Create one under Follow Up, or check your Follow Up permissions.
                        </Text>
                    )}
                </View>
            );
        }

        case 'INVOKE_AUTOMATION': {
            const automationOptions = (paths || [])
                .filter(p => p.id !== pathID)
                .map(p => ({ value: p.id, label: p.name + (p.schemaVersion === 2 ? '' : ' (legacy)') }));
            return (
                <View>
                    <Text style={styles.helpText}>
                        Starts a run of another automation, passing the inputs its trigger declares. An automation cannot invoke itself.
                    </Text>
                    <SelectInput
                        title="Automation"
                        placeholder="Select an automation..."
                        value={isBinding(config.automationID) ? undefined : config.automationID}
                        options={automationOptions}
                        onUpdate={(val) => set('automationID', val)}
                        required
                    />
                    <PersonTargetInput
                        title="Run Person (optional)"
                        variables={variables}
                        value={personBindingValue('personID')}
                        onUpdate={setPersonTarget('personID')}
                        personValue={personLiteralValue('personID')}
                        onPersonChange={(person) => set('personID', person?.id ? { id: person.id, ...person } : undefined)}
                        testID="invoke-person"
                        fromScreen="paths/steps/create"
                        fromStoreKey={storeKey}
                    />
                    {invokeInputFields.length > 0 && (
                        <View style={styles.nestedPanel}>
                            <Text style={styles.sectionLabel}>Inputs for {invokeTarget?.name || 'the automation'}</Text>
                            {invokeInputFields.map(field => (
                                field.semantic === 'personID' ? (
                                    <PersonTargetInput
                                        key={field.key}
                                        title={field.label || field.key}
                                        variables={variables}
                                        value={isBinding(config.input?.[field.key]) ? config.input[field.key] : undefined}
                                        onUpdate={(val) => setNested('input', field.key, val)}
                                        personValue={config.input?.[field.key]?.id ? config.input[field.key] : null}
                                        onPersonChange={(person) => setNested('input', field.key, person?.id ? { id: person.id, ...person } : undefined)}
                                        required={field.required === true}
                                        fromScreen="paths/steps/create"
                                        fromStoreKey={storeKey}
                                    />
                                ) : field.type === 'boolean' ? (
                                    <ToggleRow
                                        key={field.key}
                                        label={field.label || field.key}
                                        value={config.input?.[field.key] === true}
                                        onValueChange={(val) => setNested('input', field.key, val)}
                                    />
                                ) : (
                                    <AutomationBindingInput
                                        key={field.key}
                                        title={`${field.label || field.key}${field.required ? ' *' : ''}`}
                                        placeholder={field.type === 'uuid' ? 'Bind a variable or paste an ID...' : 'Enter a value...'}
                                        variables={filterVariablesForParam(variables, { type: field.type === 'uuid' ? 'uuid' : undefined })}
                                        value={isBinding(config.input?.[field.key]) ? config.input[field.key] : (config.input?.[field.key] ?? '')}
                                        onUpdate={(val) => setNested('input', field.key, val)}
                                        testID={`invoke-input-${field.key}`}
                                    />
                                )
                            ))}
                        </View>
                    )}
                </View>
            );
        }

        default:
            return null;
    }
};

const styles = StyleSheet.create((theme) => ({
    helpText: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        lineHeight: 17,
        marginBottom: 12,
    },
    sectionLabel: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 8,
        marginTop: 4,
    },
    nestedPanel: {
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 10,
        padding: 12,
        marginBottom: 12,
    },
    textWithVariableRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 6,
    },
    textInputFlex: { flex: 1 },
    inlineRow: { flexDirection: 'row', gap: 8 },
    inlineInputFlex: { flex: 1 },
    inlineSelectFixed: { width: 130 },
    updateRow: {
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        paddingBottom: 8,
        marginBottom: 8,
    },
    addRowBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 6,
    },
    addRowText: {
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: theme.colors.primary,
    },
    staleWarning: {
        fontSize: 12,
        color: theme.colors.error,
        fontStyle: 'italic',
        marginBottom: 8,
    },
}));

export default V2ActionConfigPanel;
